import { InformationCircleIcon } from "@heroicons/react/24/outline";
import React from "react";
import Input from "src/components/Shared/Forms/Inputs/Input";
import Toggle from "src/components/Shared/Forms/Toggles/Toggle";
import { getFeatureLabel, getFeatureType } from "src/helpers/planHelper";

const FeaturesForm = ({ formData, setFormData }) => {
  const updateFeatureValue = (featureCode, value) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.map((f) => (f.code === featureCode ? { ...f, value } : f)),
    }));
  };

  const getFeatureValue = (code) => formData?.features?.find((f) => f.code === code)?.value ?? 0;

  const renderNumericFeature = (feature) => {
    const label = getFeatureLabel(feature.code);
    const value = getFeatureValue(feature.code);
    const isEnabled = value !== "unlimited";

    const baseName = feature.code.replace(/_limit$/, "").replace(/_/g, " ");
    const description = `Enable maximum ${baseName} limit`;

    return (
      <div
        key={feature.code}
        className="group relative flex flex-col justify-between rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-800">{label}</div>
            <div className="text-xs text-gray-500">{description}</div>
          </div>
          <Toggle
            checked={isEnabled}
            onChange={() => updateFeatureValue(feature.code, isEnabled ? "unlimited" : 0)}
          />
        </div>

        {isEnabled ? (
          <div className="mt-4">
            <Input
              inline
              type="number"
              name={feature.code}
              label="Limit"
              value={value === "unlimited" ? 0 : value}
              onChange={(e) => {
                const num = parseInt(e.target.value) || 0;
                updateFeatureValue(feature.code, num);
              }}
              placeholder="Enter limit"
            />
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 text-xs text-gray-700 shadow-sm">
            <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-gray-700" />
            <span>
              User can add as many <strong>{label.replace(/limit$/i, "").trim()}s</strong> as they want.
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderToggleFeature = (feature) => {
    const label = getFeatureLabel(feature.code);
    const value = getFeatureValue(feature.code);

    return (
      <div
        key={feature.code}
        className="group relative flex flex-col justify-between rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-800">{label}</span>
            <span className="text-xs text-gray-500">Enable {label.toLowerCase()}</span>
          </div>
          <Toggle
            checked={!!value}
            onChange={() => updateFeatureValue(feature.code, !value)}
          />
        </div>
      </div>
    );
  };

  const limitFeatures = formData?.features?.filter((f) => getFeatureType(f.code) === "limit") || [];
  const otherFeatures = formData?.features?.filter((f) => getFeatureType(f.code) !== "limit") || [];

  return (
    <div className="animate-fade-in w-full space-y-12">
      {limitFeatures.length > 0 && (
        <section>
          <h5 className="mb-5 text-base font-semibold uppercase tracking-wide text-gray-600">Limits & Quotas</h5>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">{limitFeatures.map(renderNumericFeature)}</div>
        </section>
      )}

      {otherFeatures.length > 0 && (
        <section>
          <h5 className="mb-5 text-base font-semibold uppercase tracking-wide text-gray-600">Feature Access</h5>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">{otherFeatures.map(renderToggleFeature)}</div>
        </section>
      )}
    </div>
  );
};

export default FeaturesForm;
